import { Box, Fade, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import GoogleSignInButton from '../../components/auth/GoogleSignInButton'
import { useAuth } from '../../contexts/AuthContext'
import { apiService } from '../../services/api'
import { READER_PAGE_BACKGROUND } from '../../styles/paperBackground'
import { logger } from '../../utils/logger'

const BOOK_TITLE_HE = 'הַֽמָּֽנִיפֶ֙סְט֙ הָֽאֱנ֣וֹשִׁ֔י הָאַֽחֲר֖וֹן'
const SERIF_FONT = "'SBL Hebrew', 'Times New Roman', serif"
const NOTE_FONT = "'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const StarCluster: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.35 }}
  >
    <circle cx="20" cy="8" r="1.5" fill="var(--ornament-fill)" />
    <circle cx="12" cy="20" r="1" fill="var(--ornament-stroke)" />
    <circle cx="28" cy="18" r="1.2" fill="var(--ornament-fill)" />
    <circle cx="18" cy="30" r="0.8" fill="var(--ornament-stroke)" />
    <circle cx="30" cy="28" r="1" fill="var(--ornament-fill)" />
  </svg>
)

const ChapterOrnament: React.FC = () => (
  <svg
    width="120"
    height="24"
    viewBox="0 0 120 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.4 }}
  >
    <path d="M60 4 L66 12 L60 20 L54 12 Z" fill="var(--ornament-fill)" />
    <path
      d="M50 12 Q40 6 28 12 Q16 18 4 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M70 12 Q80 6 92 12 Q104 18 116 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <ellipse
      cx="28"
      cy="10"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-30 28 10)"
      opacity="0.6"
    />
    <ellipse cx="16" cy="15" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(20 16 15)" opacity="0.5" />
    <ellipse cx="92" cy="10" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(30 92 10)" opacity="0.6" />
    <ellipse
      cx="104"
      cy="15"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-20 104 15)"
      opacity="0.5"
    />
  </svg>
)

const EnvelopeMark: React.FC = () => (
  <svg
    width="27"
    height="27"
    viewBox="0 0 26 26"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.55 }}
  >
    <rect x="3" y="6" width="20" height="14" rx="2.5" stroke="var(--ornament-stroke)" strokeWidth="1.3" />
    <path
      d="M4 7.5 L13 13 L22 7.5"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Login: React.FC = () => {
  const navigate = useNavigate()
  const { googleLogin } = useAuth()
  const [inviteOnly, setInviteOnly] = useState(false)
  const noticeRef = useRef<HTMLDivElement>(null)

  // On short screens the centered column can push the notice below the fold, so
  // nudge it into view when it appears (no-op when it's already visible).
  useEffect(() => {
    if (!inviteOnly) {
      return
    }
    const id = requestAnimationFrame(() => {
      noticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
    return () => cancelAnimationFrame(id)
  }, [inviteOnly])

  useEffect(() => {
    const html = document.documentElement
    const prev = html.getAttribute('data-theme')
    html.setAttribute('data-theme', 'dark')
    return () => {
      if (prev) {
        html.setAttribute('data-theme', prev)
      } else {
        html.removeAttribute('data-theme')
      }
    }
  }, [])

  const handleGoogleSuccess = async (credential: string) => {
    try {
      setInviteOnly(false)
      // The login page never carries an invitation token, so the only thing that
      // can fail here for a guest is "not on the list yet" — surface that calmly
      // on-page instead of as a red error toast.
      const success = await googleLogin(credential, undefined, { silent: true })
      if (!success) {
        setInviteOnly(true)
        return
      }
      const savedUser = localStorage.getItem('user')
      const role = savedUser ? (JSON.parse(savedUser) as { role?: string }).role : undefined
      const isAuthorOrAdmin = role === 'author' || role === 'admin'

      if (!isAuthorOrAdmin) {
        navigate('/')
        return
      }

      try {
        const response = await apiService.getBooks()
        const firstBook = response.data?.[0]
        if (firstBook) {
          navigate(`/edit/${firstBook._id}`)
          return
        }
      } catch (error) {
        logger.error('Failed to load books after login:', error)
      }
      navigate('/dashboard')
    } catch (error) {
      logger.error('Google login error:', error)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: READER_PAGE_BACKGROUND,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 4,
        py: 8,
        gap: 4,
      }}
    >
      <Box
        sx={{
          width: { xs: 140, md: 180 },
          height: { xs: 140, md: 180 },
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, var(--reader-star-glow) 0%, transparent 70%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StarCluster size={48} />
      </Box>

      <Typography
        sx={{
          fontFamily: SERIF_FONT,
          fontWeight: 'normal',
          fontSize: { xs: '1.7rem', md: '2.2rem' },
          color: 'var(--reader-text-primary)',
          lineHeight: 1.5,
          letterSpacing: '0.04em',
          textAlign: 'center',
        }}
      >
        {BOOK_TITLE_HE}
      </Typography>

      <ChapterOrnament />

      <Box sx={{ mt: 2 }}>
        <GoogleSignInButton onSuccess={handleGoogleSuccess} text="signin_with" />
      </Box>

      <Fade in={inviteOnly} timeout={500} unmountOnExit>
        <Box
          ref={noticeRef}
          sx={{
            mt: 1,
            maxWidth: 360,
            px: 3.5,
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.75,
            borderRadius: 3,
            border: '1px solid var(--reader-sidebar-divider)',
            bgcolor: 'rgba(255, 255, 255, 0.025)',
            boxShadow: '0 1px 28px rgba(0, 0, 0, 0.18)',
          }}
        >
          <EnvelopeMark />
          <Typography
            sx={{
              fontFamily: NOTE_FONT,
              fontSize: '0.98rem',
              color: 'var(--reader-text-secondary)',
              lineHeight: 1.65,
              letterSpacing: '0.01em',
              textAlign: 'center',
            }}
          >
            The Last Human Manifesto opens by personal invitation. If johnjohn shared it with you, ask him for an invite
            link.
          </Typography>
        </Box>
      </Fade>
    </Box>
  )
}

export default Login
